import { Component, NgModule, OnInit, Optional } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { BankService } from 'src/app/pages/user/profile/services/bank.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { stripe_countries } from './stripe_countries';
import { Inject, Input, LOCALE_ID } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import {
	MatAutocomplete,
	MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { StripeEmbededComponent } from '../../../../../account/components/stripe-onboarding/components/stripe-embeded/stripe-embeded.component';

@Component({
	standalone: true,
	selector: 'app-select-country',
	templateUrl: './select-country.component.html',
	styleUrls: ['./select-country.component.scss'],
	imports: [
		MatFormFieldModule,
		MatCardModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
		ReactiveFormsModule,
		CommonModule,
		MatInputModule,
		MatButtonModule,
		FlexModule,
		MatSelectModule,
		MatIconModule,
		StripeEmbededComponent,
	],
})
export class SelectCountryComponent implements OnInit {
	public languageCode: string = '';
	stripeCountryList: string[] = [];
	filteredCountries: Observable<string[]> | undefined;
	countrySearchControl: FormControl = new FormControl();
	selectedCountry: string | null = null;
	isLoading: boolean = false;
	selectCountry = $localize`:@@selectCountry_page_link:does-whydonate-operate-in-my-country-1cru7w1`;
	countryLinkHref: any;
	businessTypes = [
		{
			value: 'individual',
			label: $localize`:@@businessType_personal:Individual`,
		},
		{
			value: 'company',
			label: $localize`:@@businessType_organization:Company`,
		},
		{
			value: 'non_profit',
			label: $localize`:@@businessType_charity:Non Profit`,
		},
		{
			value: 'government_entity',
			label: $localize`:@@businessType_entity:Government Entity`,
		},
	];

	availableBusinessTypes: { value: string; label: string }[] = [];
	selectedBusinessType: string | null = null;
	currentStep: number = 1;
	isStripeLoading: boolean = false;
	stripeAccountStatus: string | null = null;

	constructor(
		private _accountService: AccountService,
		private _notificationService: NotificationService,
		private _bankService: BankService,
		private router: Router,
		@Optional() private dialogRef: MatDialogRef<SelectCountryComponent>,
		@Inject(LOCALE_ID) public locale: string
	) {}

	ngOnInit(): void {
		this.setStepBasedOnStripeStatus();
		this.loadStripeCountryList();
		this.languageCode = this._accountService.getLocaleId();
		this.countryLinkHref = `https://helpdesk.whydonate.com/${this.languageCode}/article/${this.selectCountry}/`;
	}

	private setStepBasedOnStripeStatus(): void {
		if (this._accountService.checkHeaders()) {
			this._bankService.getStripeStatus().subscribe(
				(res: any) => {
					this.stripeAccountStatus = res?.data?.status;

					if (this.stripeAccountStatus === 'created' && res?.data?.charges_enabled === false) {
						this.currentStep = 2;
					} else {
						this.currentStep = 1;
					}
				},
				(error: any) => {
					this.currentStep = 1;
				}
			);
		} else {
			this.currentStep = 1;
		}
	}

	// Function to load the list of Stripe-supported countries
	private loadStripeCountryList(): void {
		try {
			// Get the locale ID, default to 'en' if not available
			const locale = this._accountService.getLocaleId() || 'en';

			// Parse the stripe_countries JSON object and get the locale-specific countries
			const stripeCountriesLocaleJson = JSON.parse(
				JSON.stringify(stripe_countries)
			)[locale];

			// Check if the locale-specific countries exist
			if (!stripeCountriesLocaleJson) {
				throw new Error(`Locale ${locale} not found in stripe_countries`);
			}

			// Convert the locale-specific countries object to an array of values
			this.stripeCountryList = Object.values(stripeCountriesLocaleJson);

			// Initialize the country filter
			this.initializeFilter();
		} catch (error) {
			// Handle any errors that occur during the country list loading
			console.error('Error loading Stripe country list:', error);
			this._notificationService.openNotification(
				$localize`:@@country_list_error:Error loading country list. Please try again.`,
				'',
				'error'
			);
		}
	}

	// Function to initialize the country filter
	private initializeFilter(): void {
		this.filteredCountries = this.countrySearchControl.valueChanges.pipe(
			startWith(''),
			map((searchTerm: string) => this.filterCountries(searchTerm || ''))
		);
	}

	// Function to filter the list of countries based on the search term
	private filterCountries(searchTerm: string): string[] {
		const lowerCaseTerm = searchTerm.toLowerCase();
		return this.stripeCountryList.filter((country) =>
			country.toLowerCase().includes(lowerCaseTerm)
		);
	}

	// Function to handle country selection
	onCountrySelect(event: any): void {
		const selectedCountryInLocale = event.option.value;
		const locale = this._accountService.getLocaleId() || 'en';

		try {
			// Parse the stripe_countries JSON object and get the locale-specific countries
			const stripeCountriesLocaleJson = JSON.parse(
				JSON.stringify(stripe_countries)
			)[locale];

			// Check if the locale-specific countries exist
			if (!stripeCountriesLocaleJson) {
				throw new Error(`Locale ${locale} not found in stripe_countries`);
			}

			// Convert JSON object to Map
			const stripeCountriesMap = new Map(
				Object.entries(stripeCountriesLocaleJson)
			);

			// Search for the key whose value contains selectedCountryInLocale
			for (let [key, value] of stripeCountriesMap) {
				if ((value as string).includes(selectedCountryInLocale)) {
					this.selectedCountry = key;
					this.updateBusinessTypes(key);
					break;
				}
			}
		} catch (error) {
			// Handle any errors that occur during the country selection
			console.error('Error selecting country:', error);
			this._notificationService.openNotification(
				$localize`:@@country_select_error:Error selecting country. Please try again.`,
				'',
				'error'
			);
		}
	}

	// Function to save the selected country and create a Stripe account
	saveCountry(): void {
		this.isStripeLoading = true;
		if (!this.selectedCountry || !this.selectedBusinessType) return;

		this._accountService
			.createStripeAccount(this.selectedCountry, this.selectedBusinessType)
			.subscribe(
				() => {
					this.currentStep = 2;
				},
				(error: any) => {
					// Handle any errors that occur during the Stripe account creation
					console.error('Error creating Stripe account:', error);
					this._notificationService.openNotification(
						$localize`:@@stripe_account_error:Error creating Stripe account. Please try again.`,
						'',
						'error'
					);
					this.isStripeLoading = false;
				}
			);
	}
	updateBusinessTypes(countryCode: string): void {
		// Only allow 'government_entity' if the selected country is United States
		const isGovernmentEntityAllowed = this.selectedCountry === 'United States';

		if (isGovernmentEntityAllowed) {
			this.availableBusinessTypes = [...this.businessTypes];
		} else {
			this.availableBusinessTypes = this.businessTypes.filter(
				(type) => type.value !== 'government_entity'
			);
		}

		// Reset selected type if it's not available anymore
		if (
			!this.availableBusinessTypes.find(
				(bt) => bt.value === this.selectedBusinessType
			)
		) {
			this.selectedBusinessType = null;
		}
	}
}
